import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Menu, X, Home, List, User, Wallet, LogIn, UserPlus, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './Navbar.css';

interface NavbarProps {
  className?: string;
}

const Navbar: React.FC<NavbarProps> = ({ className = '' }) => {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = (): void => {
    setIsMenuOpen(false);
  };

  const openAuthModal = (mode: 'signin' | 'signup'): void => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = (): void => {
    setShowAuthModal(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      closeMenu();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      <nav className={`navbar ${className}`}>
        <div className="nav-container">
          {/* Logo */}
          <motion.div
            className="nav-logo"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a href="/" className="logo-link">
              <BookOpen size={28} />
              <span>Echoes Of Lunaria</span>
            </a>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="nav-links desktop">
            <motion.a
              href="/"
              className="nav-link"
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
            >
              <Home size={18} />
              Home
            </motion.a>
            <motion.a
              href="/chapters"
              className="nav-link"
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
            >
              <List size={18} />
              Chapters
            </motion.a>
            {user && (
              <>
                <motion.a
                  href="/wallet"
                  className="nav-link"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  <Wallet size={18} />
                  Wallet
                </motion.a>
                <motion.a
                  href="/profile"
                  className="nav-link"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  <User size={18} />
                  Profile
                </motion.a>
              </>
            )}
          </div>

          {/* Authentication */}
          <div className="nav-auth desktop">
            {user ? (
              <div className="user-menu">
                <span className="user-greeting">
                  Welcome, {user.displayName || user.email}!
                </span>
                <motion.button
                  className="auth-btn logout"
                  onClick={handleLogout}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LogOut size={16} />
                  Logout
                </motion.button>
              </div>
            ) : (
              <div className="auth-buttons">
                <motion.button
                  className="auth-btn signin"
                  onClick={() => openAuthModal('signin')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LogIn size={16} />
                  Sign In
                </motion.button>
                <motion.button
                  className="auth-btn signup"
                  onClick={() => openAuthModal('signup')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <UserPlus size={16} />
                  Sign Up
                </motion.button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="mobile-menu-btn"
            onClick={toggleMenu}
            whileTap={{ scale: 0.95 }}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mobile-nav-links">
                <motion.a
                  href="/"
                  className="mobile-nav-link"
                  onClick={closeMenu}
                  whileTap={{ scale: 0.95 }}
                >
                  <Home size={18} />
                  Home
                </motion.a>
                <motion.a
                  href="/chapters"
                  className="mobile-nav-link"
                  onClick={closeMenu}
                  whileTap={{ scale: 0.95 }}
                >
                  <List size={18} />
                  Chapters
                </motion.a>
                {user && (
                  <>
                    <motion.a
                      href="/wallet"
                      className="mobile-nav-link"
                      onClick={closeMenu}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Wallet size={18} />
                      Wallet
                    </motion.a>
                    <motion.a
                      href="/profile"
                      className="mobile-nav-link"
                      onClick={closeMenu}
                      whileTap={{ scale: 0.95 }}
                    >
                      <User size={18} />
                      Profile
                    </motion.a>
                  </>
                )}
              </div>

              <div className="mobile-auth">
                {user ? (
                  <div className="mobile-user-menu">
                    <span className="mobile-user-greeting">
                      Welcome, {user.displayName || user.email}!
                    </span>
                    <motion.button
                      className="mobile-auth-btn logout"
                      onClick={handleLogout}
                      whileTap={{ scale: 0.95 }}
                    >
                      <LogOut size={16} />
                      Logout
                    </motion.button>
                  </div>
                ) : (
                  <div className="mobile-auth-buttons">
                    <motion.button
                      className="mobile-auth-btn signin"
                      onClick={() => {
                        openAuthModal('signin');
                        closeMenu();
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <LogIn size={16} />
                      Sign In
                    </motion.button>
                    <motion.button
                      className="mobile-auth-btn signup"
                      onClick={() => {
                        openAuthModal('signup');
                        closeMenu();
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <UserPlus size={16} />
                      Sign Up
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={closeAuthModal} 
        mode={authMode}
        onSwitchMode={setAuthMode}
      />
    </>
  );
};

// Auth Modal Component
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'signin' | 'signup';
  onSwitchMode: (mode: 'signin' | 'signup') => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, mode, onSwitchMode }) => {
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else {
        await signUp(email, password, displayName);
      }
      onClose();
    } catch (error: any) {
      setError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      await signInWithGoogle();
      onClose();
    } catch (error: any) {
      setError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <motion.div
        className="auth-modal"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-modal-header">
          <h2>{mode === 'signin' ? 'Sign In' : 'Sign Up'}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'signup' && (
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your display name"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? 'Loading...' : mode === 'signin' ? 'Sign In' : 'Sign Up'}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button 
          type="button" 
          className="google-auth-btn"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          Continue with Google
        </button>

        <div className="auth-switch">
          {mode === 'signin' ? (
            <p>
              Don't have an account?{' '}
              <button type="button" onClick={() => onSwitchMode('signup')}>
                Sign Up
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button type="button" onClick={() => onSwitchMode('signin')}>
                Sign In
              </button>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Navbar;